// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/),
);

export function register(config) {
  if (
    (process.env.NODE_ENV === 'production' || process.env.REACT_APP_TEST_SW === 'true') &&
    'serviceWorker' in navigator
  ) {
    // The URL constructor is available in all browsers that support SW.
    // const publicUrl = new URL(process.env.REACT_APP_PUBLIC_URL, window.location.href);
    // if (publicUrl.origin !== window.location.origin) {
    //   // Our service worker won't work if PUBLIC_URL is on a different origin
    //   // from what our page is served on. This might happen if a CDN is used to
    //   // serve assets; see https://github.com/facebook/create-react-app/issues/2374
    //   return;
    // }

    // When a new SW takes control of the page (via skipWaiting + clientsClaim
    // in service-worker.js), reload exactly once so the page's running JS
    // matches the new SW's precache. Without this, the page can keep running
    // bundle code that drifts from what the new SW knows how to serve, which
    // is a common source of post-deploy offline failures.
    //
    // Skip the reload on first-ever install (no prior controller). Otherwise
    // clientsClaim() on the very first SW activation fires controllerchange
    // and reloads a brand-new tab the user just opened.
    const hadControllerAtRegistration = Boolean(navigator.serviceWorker.controller);
    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!hadControllerAtRegistration) return;
      if (refreshing) return;
      refreshing = true;
      window.location.reload();
    });

    // Defensive recovery from stale-chunk failures. If the page's running JS
    // references a chunk hash the active SW's precache doesn't have (e.g. a
    // deploy landed mid-session and controllerchange didn't fire in time, or
    // a user had a tab from a previous deploy without this handler), webpack
    // throws a ChunkLoadError when lazy() tries to import the missing chunk.
    //
    // Recovery strategy: when we see a ChunkLoadError AND we're online, do
    // one hard reload to pick up fresh HTML and bundles. A sessionStorage
    // cooldown prevents reload loops if the fresh bundles also fail.
    // Offline crashes are not reloaded — reloading offline would only make
    // things worse.
    const CHUNK_RELOAD_COOLDOWN_MS = 30000;
    const CHUNK_RELOAD_KEY = 'sw_chunk_reload_ts';
    const isChunkLoadError = (error) => {
      if (!error) return false;
      if (error.name === 'ChunkLoadError') return true;
      return /Loading chunk [\w\d]+ failed/i.test(String(error.message || ''));
    };
    const handleChunkLoadError = (error) => {
      if (!isChunkLoadError(error)) return;
      if (!navigator.onLine) return;
      const lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
      if (Date.now() - lastReload < CHUNK_RELOAD_COOLDOWN_MS) return;
      sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
      window.location.reload();
    };
    window.addEventListener('error', (event) => handleChunkLoadError(event.error));
    window.addEventListener('unhandledrejection', (event) => handleChunkLoadError(event.reason));

    window.addEventListener('load', () => {
      // const swUrl = `${process.env.REACT_APP_PUBLIC_URL}/service-worker.js`;
      const swUrl = `/service-worker.js`;

      if (isLocalhost) {
        // This is running on localhost. Let's check if a service worker still exists or not.
        checkValidServiceWorker(swUrl, config);
      } else {
        // Is not localhost. Just register service worker
        registerValidSW(swUrl, config);
      }
    });
  }
}

function registerValidSW(swUrl, config) {
  navigator.serviceWorker
    .register(swUrl, { updateViaCache: 'none' })
    .then((registration) => {
      // eslint-disable-next-line no-param-reassign
      registration.onupdatefound = () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }
        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else if (config && config.onSuccess) {
              // Everything has been precached on first install.
              config.onSuccess(registration);
            }
          }
        };
      };
    })
    .catch(() => {
      // SW registration failure leaves the app working without offline support;
      // Chrome DevTools → Application → Service Workers shows the cause.
    });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, {
    headers: { 'Service-Worker': 'script' },
  })
    .then((response) => {
      // Ensure service worker exists, and that we really are getting a JS file.
      const contentType = response.headers.get('content-type');
      if (response.status === 404 || (contentType != null && contentType.indexOf('javascript') === -1)) {
        // No service worker found. Probably a different app. Reload the page.
        navigator.serviceWorker.ready.then((registration) => {
          registration.unregister().then(() => {
            window.location.reload();
          });
        });
      } else {
        // Service worker found. Proceed as normal.
        registerValidSW(swUrl, config);
      }
    })
    .catch(() => {
      // Offline at SW check time; app falls back to whatever's already cached.
    });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch(() => {
        // unregister() failure has no user-recoverable action; leave it silent.
      });
  }
}
